import { useCheckoutStore } from "../stores/checkout-store";
import { decodeJWT, getAccessToken } from "./token";

const EXPIRED_SESSION_USER_ID_KEY = "mozio-expired-session-user-id";

/**
 * `sessionStorage`, not `localStorage`: the value has to survive the full-page
 * round trip to `/user/login` and back, but must not outlive the tab.
 */
const getUserIdFromAccessToken = (): string | undefined => {
  const token = getAccessToken();
  if (!token) {
    return undefined;
  }

  try {
    const { user_id: userId } = decodeJWT(token).payload;
    // decodeJWT casts an unchecked JSON.parse to DecodedJWT, so the payload can
    // be missing user_id however the type reads. String(undefined) would stash
    // "undefined" and later read as a genuine account change.
    if (typeof userId !== "number" && typeof userId !== "string") {
      return undefined;
    }
    return String(userId);
  } catch (error) {
    // decodeJWT throws on a malformed token. Never let that break the caller —
    // both callers run on paths (401 handling, app bootstrap) where throwing
    // would be worse than skipping the check.
    console.error("Could not decode access token:", error);
    return undefined;
  }
};

/**
 * Remembers who owned the session that just expired.
 *
 * MUST be called before `removeTokens()` — it reads the access token that is
 * about to be discarded.
 */
export const stashExpiredSessionUserId = () => {
  try {
    const userId = getUserIdFromAccessToken();
    if (!userId) {
      return;
    }
    window.sessionStorage.setItem(EXPIRED_SESSION_USER_ID_KEY, userId);
  } catch (error) {
    console.error("Could not stash expired session user:", error);
  }
};

/**
 * Runs once per page load. If the user signed back in as somebody else after a
 * session expiry, clears the persisted checkout form (DEV-12536).
 *
 * Resets the whole store rather than just the travel-agent fields: it also holds
 * the previous user's passenger names, email, phone and selected payment method,
 * and none of that belongs to whoever signed in. This matches what `useLogout`
 * already does on a deliberate sign-out — an account switch should not leave
 * more behind than logging out does.
 *
 * `search` is repopulated from the URL by the route's `validateSearch`, which
 * runs after this, so the trip being booked survives.
 */
export const reconcileSessionIdentity = () => {
  try {
    const previousUserId = window.sessionStorage.getItem(
      EXPIRED_SESSION_USER_ID_KEY,
    );
    if (!previousUserId) {
      return;
    }

    // Clear first: this is a one-shot comparison, and every path below is a
    // reason not to check again.
    window.sessionStorage.removeItem(EXPIRED_SESSION_USER_ID_KEY);

    // No token means they came back without signing in — nothing to compare.
    const currentUserId = getUserIdFromAccessToken();
    if (!currentUserId || currentUserId === previousUserId) {
      return;
    }

    useCheckoutStore.getState().reset();
  } catch (error) {
    console.error("Could not reconcile session identity:", error);
  }
};
